import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCartStore } from '@/stores/cartStore'
import { useShopStore } from '@/stores/shopStore'
import { useAppStore } from '@/store/appStore'
import { useWishlistStore } from '@/stores/wishlistStore'
import { apiUrl } from '@/config'
import { saveWishlistToken } from '@/utils/wishlistHistory'
import './ItemListPanel.css'

interface ItemListPanelProps {
  isOpen: boolean
  onClose: () => void
  onOpenCart: () => void
  shopId: string
}

/**
 * ItemListPanel — Slide-in panel showing all marked/cart items.
 * Tap an item to navigate the viewer to its frame.
 */
export function ItemListPanel({ isOpen, onClose, onOpenCart: _onOpenCart, shopId }: ItemListPanelProps) {
  const { t } = useTranslation('delivery')
  const items = useCartStore((s) => s.items)
  const removeItem = useCartStore((s) => s.removeItem)
  const addToCart = useCartStore((s) => s.addToCart)
  const removeFromCart = useCartStore((s) => s.removeFromCart)
  const clearShopItems = useCartStore((s) => s.clearShopItems)
  const navigate = useNavigate()

  const setManuallySelectedFrame = useAppStore((s) => s.setManuallySelectedFrame)
  const centerViewportOnFrame = useAppStore((s) => s.centerViewportOnFrame)

  const currentSectionId = useAppStore((s) => s.currentSectionId)
  const markerLabelsVisible = useAppStore((s) => s.markerLabelsVisible)
  const toggleMarkerLabels = useAppStore((s) => s.toggleMarkerLabels)

  const [navigatingItemId, setNavigatingItemId] = useState<string | null>(null)

  const activeWishlists = useWishlistStore((s) => s.activeWishlists)

  // Filter to current shop + section
  const shopItems = items.filter((i) => i.shopId === shopId && i.sectionId === String(currentSectionId))

  const handleNavigateToItem = useCallback((item: typeof shopItems[0]) => {
    // Highlight the tapped item
    setNavigatingItemId(item.id)

    // Navigate the viewer to the item's original frame
    if (setManuallySelectedFrame) {
      setManuallySelectedFrame(item.frameIndex)
      centerViewportOnFrame(item.frameIndex)
    }

    // Close panel after a short delay so user sees the item in the viewer
    setTimeout(() => {
      setNavigatingItemId(null)
      onClose()
    }, 400)
  }, [setManuallySelectedFrame, centerViewportOnFrame, onClose])

  const handleToggleCart = useCallback((id: string, inCart: boolean) => {
    if (inCart) {
      removeFromCart(id)
    } else {
      addToCart(id)
    }
  }, [addToCart, removeFromCart])

  const handleClearAll = useCallback(() => {
    clearShopItems(shopId)
  }, [shopId, clearShopItems])

  const [sharing, setSharing] = useState(false)
  const [showShareForm, setShowShareForm] = useState(false)
  const [collapsedWishlists, setCollapsedWishlists] = useState<Set<string>>(new Set())

  const toggleWishGroup = useCallback((token: string) => {
    setCollapsedWishlists((prev) => {
      const next = new Set(prev)
      if (next.has(token)) next.delete(token)
      else next.add(token)
      return next
    })
  }, [])
  const [shareFormName, setShareFormName] = useState('')
  const [shareFormTitle, setShareFormTitle] = useState('')

  const handleShareWishList = useCallback(() => {
    const allItems = useCartStore.getState().items
    if (allItems.length === 0) return
    setShareFormName('')
    setShareFormTitle('')
    setShowShareForm(true)
  }, [])

  const submitShareWishList = useCallback(async () => {
    if (sharing) return
    const senderName = shareFormName.trim()
    const title = shareFormTitle.trim()
    setShowShareForm(false)
    setSharing(true)
    try {
      const allItems = useCartStore.getState().items
      const { shops, publicShops } = useShopStore.getState()
      const { availableDatasets } = useAppStore.getState()

      // Cart items store dataset IDs as "shopId". Resolve to real shop DB IDs
      // so the wishlist page can link to /shop/{realShopId}.
      const resolveRealShopId = (datasetId: string) => {
        const ds = availableDatasets.find(d => d.id === datasetId)
        return ds?.shopId || datasetId
      }

      const resolveShopName = (realShopId: string) => {
        const s = shops.find((sh) => sh.id === realShopId) || publicShops.find((sh) => sh.id === realShopId)
        return s?.name || realShopId
      }

      const payload = {
        senderName,
        title,
        items: allItems.map((item, idx) => {
          const realShopId = resolveRealShopId(item.shopId)
          return {
            name: item.name || t('itemList.itemFallbackName', { number: idx + 1 }),
            price: item.price,
            frameIndex: item.frameIndex,
            sectionId: item.sectionId,
            shopId: realShopId,
            shopName: resolveShopName(realShopId),
            thumbnailUrl: item.thumbnailDataUrl,
            imagePixelX: item.imagePixelX,
            imagePixelY: item.imagePixelY,
            radius: item.radius,
            point3D: item.point3D ?? null,
          }
        }),
      }
      const res = await fetch(apiUrl('/api/wishlists'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error('Failed to create wishlist')
      const data = await res.json()
      const shareUrl = `${window.location.origin}/w/${data.shareToken}`

      // Persist token so home page can list it
      const shopIds = [...new Set(payload.items.map((i) => i.shopId))]
      saveWishlistToken({
        token: data.shareToken,
        shopName: shopIds.length === 1 ? resolveShopName(shopIds[0]) : '',
        itemCount: allItems.length,
        multiShop: shopIds.length > 1,
        createdAt: new Date().toISOString(),
        type: 'created',
        senderName,
        title,
      })

      const shopLabel = shopIds.length === 1
        ? resolveShopName(shopIds[0])
        : t('wishlist.multipleShops', { count: shopIds.length })

      const msg = t('wishlist.whatsappShareText', {
        shopName: shopLabel,
        count: allItems.length,
        url: shareUrl,
      })
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank')
    } catch (err) {
      console.error('Failed to share wish list:', err)
    } finally {
      setSharing(false)
    }
  }, [sharing, shareFormName, shareFormTitle, t])

  return (
    <div className={`item-list-panel ${isOpen ? 'open' : ''}`}>

      {/* ── Share Wish List form overlay ── */}
      {showShareForm && (
        <div className="item-list-share-overlay">
          <div className="item-list-share-form">
            <h4 className="item-list-share-form-title">📤 {t('wishlist.shareButton')}</h4>
            <label className="item-list-share-label">
              {t('wishlist.labelSenderName')}
              <input
                className="item-list-share-input"
                type="text"
                value={shareFormName}
                onChange={(e) => setShareFormName(e.target.value)}
                placeholder={t('wishlist.placeholderSenderName')}
                autoFocus
              />
            </label>
            <label className="item-list-share-label">
              {t('wishlist.labelTitle')}
              <input
                className="item-list-share-input"
                type="text"
                value={shareFormTitle}
                onChange={(e) => setShareFormTitle(e.target.value)}
                placeholder={t('wishlist.placeholderTitle')}
                onKeyDown={(e) => e.key === 'Enter' && submitShareWishList()}
              />
            </label>
            <div className="item-list-share-form-actions">
              <button
                className="item-list-share-btn-cancel"
                onClick={() => setShowShareForm(false)}
              >
                {t('common:cancel')}
              </button>
              <button
                className="item-list-share-btn-send"
                onClick={submitShareWishList}
                disabled={sharing}
              >
                💬 {t('wishlist.sendButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="item-list-header">
        <h3>{t('itemList.title', { count: shopItems.length })}</h3>
        <div className="item-list-header-actions">
          <button
            className={`item-list-labels-toggle ${markerLabelsVisible ? '' : 'labels-off'}`}
            onClick={toggleMarkerLabels}
            title={markerLabelsVisible ? t('itemList.hideLabels') : t('itemList.showLabels')}
          >
            🏷️
          </button>
          <button className="item-list-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <div className="item-list-body">
        {shopItems.length === 0 ? (
          <div className="item-list-empty">
            {t('itemList.emptyMessage')}
          </div>
        ) : (
          shopItems.map((item, idx) => (
            <div
              key={item.id}
              className={`item-list-row ${navigatingItemId === item.id ? 'navigating' : ''} ${!item.inCart ? 'unchecked' : ''}`}
              onClick={(e) => {
                // Don't navigate if user tapped the checkbox or action buttons
                const target = e.target as HTMLElement
                if (target.closest('input') || target.closest('button')) return
                handleNavigateToItem(item)
              }}
            >
              {item.thumbnailDataUrl ? (
                <img src={item.thumbnailDataUrl} alt="" className="item-list-thumb" />
              ) : (
                <div className="item-list-thumb-placeholder">📦</div>
              )}
              <div className="item-list-info">
                <div className="item-list-name">
                  {item.name || t('itemList.itemFallbackName', { number: idx + 1 })}
                </div>
                <div className="item-list-meta">
                  {t('itemList.frameMeta', { frameIndex: item.frameIndex })}{item.inCart ? ` • ${t('itemList.inCart')}` : ''}
                </div>
                {item.price !== null && (
                  <div className="item-list-price">{t('itemList.priceLabel', { price: item.price.toFixed(2) })}</div>
                )}
              </div>
              <button
                className="item-list-navigate"
                onClick={(e) => {
                  e.stopPropagation()
                  handleNavigateToItem(item)
                }}
                title={t('itemList.goToItem')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="3" />
                  <line x1="12" y1="2" x2="12" y2="6" />
                  <line x1="12" y1="18" x2="12" y2="22" />
                  <line x1="2" y1="12" x2="6" y2="12" />
                  <line x1="18" y1="12" x2="22" y2="12" />
                </svg>
              </button>
              <button
                className={`item-list-checkbox-btn ${item.inCart ? 'checked' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  handleToggleCart(item.id, item.inCart)
                }}
              >
                {item.inCart && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
              <button
                className="item-list-delete"
                onClick={(e) => {
                  e.stopPropagation()
                  removeItem(item.id)
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
              </button>
            </div>
          ))
        )}

        {/* ── Wish list groups (read-only, per person) ── */}
        {activeWishlists.filter((wl) => wl.visible && wl.items.length > 0).map((wl) => {
          const isCollapsed = collapsedWishlists.has(wl.token)
          return (
          <div key={wl.token} className="item-list-wish-group">
            <button
              className="item-list-wish-group-header"
              style={{ borderLeftColor: wl.color }}
              onClick={() => toggleWishGroup(wl.token)}
            >
              <span className="item-list-wish-group-dot" style={{ background: wl.color }} />
              <span className="item-list-wish-group-name">
                💝 {wl.senderName || t('wishBanner.unknown')}
              </span>
              <span className="item-list-wish-group-count">
                {wl.items.length} {t('wishGroup.items')}
              </span>
              <svg
                className={`item-list-wish-group-chevron ${isCollapsed ? 'collapsed' : ''}`}
                width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {!isCollapsed && wl.items.map((wItem, idx) => (
              <div
                key={`${wl.token}-${idx}`}
                className="item-list-row item-list-wish-row"
                onClick={() => {
                  // Navigate to the frame where this wish item is
                  if (setManuallySelectedFrame) {
                    setManuallySelectedFrame(wItem.frameIndex)
                    centerViewportOnFrame(wItem.frameIndex)
                  }
                  setTimeout(() => onClose(), 400)
                }}
              >
                {wItem.thumbnailUrl ? (
                  <img src={wItem.thumbnailUrl} alt="" className="item-list-thumb" />
                ) : (
                  <div className="item-list-thumb-placeholder" style={{ color: wl.color }}>💝</div>
                )}
                <div className="item-list-info">
                  <div className="item-list-name">{wItem.name || t('wishlist.itemFallback', { number: idx + 1 })}</div>
                  <div className="item-list-meta">
                    {t('itemList.frameMeta', { frameIndex: wItem.frameIndex })}
                  </div>
                  {wItem.price !== null && (
                    <div className="item-list-price">${wItem.price.toFixed(2)}</div>
                  )}
                  {wItem.note && (
                    <div className="item-list-meta" style={{ fontStyle: 'italic' }}>📝 {wItem.note}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
          )
        })}
      </div>

      {shopItems.length > 0 && (
        <div className="item-list-footer">
          <div className="item-list-footer-row">
            <button className="item-list-btn-share" onClick={handleShareWishList} disabled={sharing}>
              {sharing ? '⏳' : '📤'} {t('wishlist.shareButton')}
            </button>
            <button className="item-list-btn-clear" onClick={handleClearAll}>
              {t('itemList.clearAll')}
            </button>
          </div>
          <button
            className="item-list-btn-checkout"
            onClick={() => {
              onClose()
              navigate(`/checkout/${shopId}`)
            }}
          >
            {t('itemList.proceedToCheckout', { count: shopItems.filter((i) => i.inCart).length })}
          </button>
        </div>
      )}
    </div>
  )
}
